
import React, { useEffect, useState } from 'react';
import { ChevronRight, ArrowRight, Shield, Zap, Target, MessageSquare, Users, Star, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import ROISimulator from '../components/ROISimulator';

const Home: React.FC = () => {
  const stats = [
    { label: "Leads générés / mois", value: "1000", suffix: "+" },
    { label: "Contacts traités / an", value: "500k", suffix: "+" },
    { label: "Augmentation CA moyenne", value: "15", suffix: "%" }
  ];

  const partners = [
    { name: "MapDuo", logo: "https://storage.googleapis.com/a1aa/image/mapduo_logo.png", alt: "Logo MapDuo" },
    { name: "Monekat", logo: "https://storage.googleapis.com/a1aa/image/monekat_logo.png", alt: "Logo Monekat" },
    { name: "Wondergifts", logo: "https://storage.googleapis.com/a1aa/image/wondergifts_logo.png", alt: "Logo Wondergifts" },
    { name: "See U Better", logo: "https://storage.googleapis.com/a1aa/image/seeubetter_logo.png", alt: "Logo See U Better" }
  ];

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden">
        {/* Network Animated Background */}
        <div className="absolute inset-0 bg-network-dots opacity-40 -z-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-10">
            <div className="inline-flex items-center bg-white border border-sand-300 px-4 py-2 rounded-full text-prime-blue text-xs font-bold tracking-widest uppercase shadow-sm">
              <Star className="w-4 h-4 mr-2 fill-prime-blue text-prime-blue" />
              Excellence Opérationnelle
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold font-heading text-prime-dark leading-[1.1]">
              Ne cherchez plus un prestataire. Trouvez votre <span className="text-transparent bg-clip-text bg-gradient-to-r from-prime-blue to-prime-green">partenaire de croissance.</span>
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
              L'excellence opérationnelle alliée à l'intelligence émotionnelle. 
              <span className="text-prime-dark font-bold"> PRIMECONNECT</span> réinvente l'expérience client depuis ses hubs mondiaux.
            </p>

            <div className="flex flex-col sm:flex-row gap-5">
              <button 
                onClick={() => document.getElementById('roi-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn-prime hover:brightness-110 text-white px-10 py-5 rounded-full font-bold text-lg transition-all flex items-center justify-center group shadow-xl shadow-prime-blue/20"
              >
                Simuler mon ROI
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link 
                to="/solutions"
                className="bg-white border-2 border-sand-300 hover:border-prime-blue text-slate-700 hover:text-prime-blue px-10 py-5 rounded-full font-bold text-lg transition-all text-center shadow-sm"
              >
                Découvrir nos expertises
              </Link>
            </div>

            <div className="flex items-center space-x-8 pt-4">
              <div className="flex -space-x-3">
                 {[1,2,3,4].map(i => (
                   <div key={i} className="w-12 h-12 rounded-full border-2 border-white bg-sand-200 overflow-hidden shadow-sm">
                      <img src={`https://i.pravatar.cc/150?u=pc${i}`} alt="Agent" />
                   </div>
                 ))}
              </div>
              <div className="text-sm font-semibold text-slate-500">
                 <span className="text-prime-dark block font-bold">Agents expérimentés</span>
                 Cameroun • France • Canada
              </div>
            </div>
          </div>

          <div className="relative group">
            <div className="absolute -inset-4 bg-prime-blue/5 rounded-[3rem] blur-2xl group-hover:bg-prime-blue/10 transition-all"></div>
            <div className="relative z-10 animate-float">
               <img 
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80&w=1200" 
                alt="PrimeConnect Excellence" 
                className="rounded-[3rem] shadow-2xl border-4 border-white object-cover h-[600px] w-full"
               />
               <div className="absolute bottom-8 -right-8 bg-prime-dark text-white p-8 rounded-[2rem] shadow-2xl max-w-xs border border-white/10 backdrop-blur-md hidden sm:block">
                  <div className="flex items-center space-x-4 mb-4">
                     <div className="p-3 bg-gradient-to-br from-prime-blue to-prime-green rounded-xl">
                        <Users className="w-6 h-6 text-white" />
                     </div>
                     <h3 className="font-bold font-heading">Hub Performance</h3>
                  </div>
                  <p className="text-sand-100/60 text-sm mb-4 leading-relaxed italic">
                    "Plus qu'une externalisation, PrimeConnect est devenu le moteur de notre service client."
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-prime-green font-bold text-xs uppercase">+98% FCR</span>
                    <div className="flex space-x-1">
                      {[1,2,3,4,5].map(s => <Star key={s} className="w-3 h-3 text-amber-400 fill-amber-400" />)}
                    </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-24 bg-white border-y border-sand-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-slate-400 text-xs font-bold uppercase tracking-[0.3em] mb-16">
            Ils nous confient leur bien le plus précieux : leurs clients
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 items-center justify-center opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
             {partners.map((partner, i) => (
               <div key={i} className="flex items-center justify-center p-4">
                  <img 
                    src={partner.logo} 
                    alt={partner.alt} 
                    className="max-h-16 w-auto object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `<span class="font-bold text-prime-dark font-heading text-xl">${partner.name}</span>`;
                    }}
                  />
               </div>
             ))}
          </div>

          <div className="grid md:grid-cols-3 gap-12 mt-32">
            {stats.map((stat, idx) => (
              <div key={idx} className="p-10 bg-sand-50 rounded-[2.5rem] border border-sand-200 group hover:border-prime-blue transition-all">
                <div className="text-6xl font-bold text-prime-dark font-heading mb-4 group-hover:text-prime-blue transition-colors">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-slate-500 font-bold uppercase tracking-widest text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-24 max-w-3xl mx-auto">
            <h2 className="text-5xl font-bold text-prime-dark font-heading mb-8">
              Plus qu'un Call Center, une extension de votre marque.
            </h2>
            <div className="w-20 h-1.5 btn-prime mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-10">
            {[
              { 
                title: "Ambassadeurs, pas robots.", 
                text: "Nos talents sont formés à votre culture. Accent neutre, empathie réelle et intelligence situationnelle.",
                icon: <Users size={32} /> 
              },
              { 
                title: "La Tech au service de l'Humain.", 
                text: "Puissance technologique des géants avec l'agilité d'une start-up. L'IA augmente nos agents.",
                icon: <Zap size={32} /> 
              },
              { 
                title: "Transparence Totale.", 
                text: "Dashboard temps réel, écoute d'appels, KPIs suivis à la seconde. Zéro boîte noire.",
                icon: <Shield size={32} /> 
              }
            ].map((card, idx) => (
              <div key={idx} className="p-12 rounded-[3rem] bg-white border border-sand-200 hover:border-prime-blue transition-all group shadow-sm hover:shadow-xl hover:shadow-prime-blue/5">
                <div className="w-16 h-16 bg-sand-100 rounded-2xl flex items-center justify-center mb-10 text-prime-blue group-hover:btn-prime group-hover:text-white transition-all shadow-sm">
                  {card.icon}
                </div>
                <h3 className="text-2xl font-bold text-prime-dark mb-6 font-heading">{card.title}</h3>
                <p className="text-slate-500 leading-relaxed font-medium">
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section id="roi-section" className="py-32 bg-sand-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <ROISimulator />
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-32 bg-prime-dark text-white relative overflow-hidden">
         <div className="absolute inset-0 bg-network-dots opacity-5"></div>
         <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-20 items-center">
               <div>
                  <h2 className="text-5xl font-bold font-heading mb-8">Une présence globale pour un impact local.</h2>
                  <p className="text-xl text-sand-100/60 mb-12 leading-relaxed">
                     De Paris à Montréal, en passant par notre hub stratégique à Yaoundé, nous offrons une continuité de service 24/7/365 avec une expertise culturelle sans faille.
                  </p>
                  <div className="space-y-6">
                     {['Yaoundé : Hub d\'Excellence & Production', 'Paris : Stratégie & Management de proximité', 'Montréal : Support Nord-Américain'].map((item, i) => (
                       <div key={i} className="flex items-center space-x-4">
                          <Globe className="text-prime-green w-6 h-6" />
                          <span className="font-bold tracking-wide">{item}</span>
                       </div>
                     ))}
                  </div>
               </div>
               <div className="bg-white/5 p-2 rounded-[3rem] border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1526772662000-3f88f10405ff?auto=format&fit=crop&q=80&w=800" 
                    alt="Network" 
                    className="rounded-[2.8rem] opacity-80"
                  />
               </div>
            </div>
         </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
         <div className="max-w-5xl mx-auto px-4 text-center btn-prime rounded-[4rem] p-20 relative overflow-hidden shadow-2xl shadow-prime-blue/30">
            <div className="absolute inset-0 bg-network-dots opacity-20"></div>
            <h2 className="text-4xl md:text-6xl font-bold text-white font-heading mb-10 relative z-10 leading-tight">
              Prêt à booster votre expérience client ?
            </h2>
            <Link to="/contact" className="inline-block bg-white text-prime-dark px-14 py-6 rounded-full font-bold text-2xl hover:bg-sand-50 transition-all shadow-2xl relative z-10">
              Parlons de votre projet
            </Link>
         </div>
      </section>
    </div>
  );
};

export default Home;
